import { useEffect, useRef } from "react";

const FOCUSABLE_SELECTORS = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "[tabindex]:not([tabindex='-1'])",
  "details > summary",
].join(", ");

function getFocusableElements(container) {
  if (!container) return [];
  return Array.from(container.querySelectorAll(FOCUSABLE_SELECTORS)).filter(
    (el) => !el.closest("[hidden]") && el.offsetParent !== null
  );
}

// Captures the element that had focus when the dialog opened and restores
// focus to it on close. Also focuses the dialog container so keyboard users
// land inside the dialog on open. Traps Tab/Shift+Tab within the container
// while open so focus cannot leave the modal (aria-modal="true" behaviour).
export function useDialogFocusRestoration(isOpen, containerRef) {
  const previouslyFocusedRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;

    previouslyFocusedRef.current =
      typeof document !== "undefined" && document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;

    const node = containerRef?.current;
    if (node && typeof node.focus === "function") {
      try {
        node.focus({ preventScroll: true });
      } catch {
        node.focus();
      }
    }

    function handleTabTrap(event) {
      if (event.key !== "Tab") return;
      const container = containerRef?.current;
      if (!container) return;

      const focusable = getFocusableElements(container);
      if (focusable.length === 0) {
        event.preventDefault();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey) {
        if (document.activeElement === first || document.activeElement === container) {
          event.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last || document.activeElement === container) {
          event.preventDefault();
          first.focus();
        }
      }
    }

    document.addEventListener("keydown", handleTabTrap);

    return () => {
      document.removeEventListener("keydown", handleTabTrap);
      const target = previouslyFocusedRef.current;
      previouslyFocusedRef.current = null;
      if (target && typeof target.focus === "function" && document.contains(target)) {
        try {
          target.focus({ preventScroll: true });
        } catch {
          target.focus();
        }
      }
    };
  }, [isOpen, containerRef]);
}
